import { INITIAL_PAGE_STATE } from 'constants/constants';
import { stateStore } from 'global/state.store';
import { sendPostMessage } from 'helpers/post-message.helper';
import { FunctionComponent, h } from 'preact';
import {
  useContext, useEffect, useMemo, useState,
} from 'preact/hooks';
import { useApi } from 'services/use-api';
import { ApplicationContext } from 'services/use-application-setup';
import { useTranslation } from 'services/use-translation';
import './report.scss';
import { isMobile } from 'helpers/detect-user-agent';
import { layoutStore } from '../../global/layout.store';

const NO_SCREENSHOT = 'no_screenshot_available';

export const ReportView: FunctionComponent = () => {
  const { code, href, settings } = useContext(ApplicationContext);
  const [viewportScreenshot, setViewportScreenshot] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [formError, setFormError] = useState<{ email: string; text: string }>(
    null,
  );

  const [text, setText] = useState('');
  const [email, setEmail] = useState('');

  const Api = useApi();
  const { translate } = useTranslation();

  const goBack = () => {
    stateStore.state.next(INITIAL_PAGE_STATE);
  };

  const clearErrors = () => {
    setFormError(null);
  };

  useEffect(() => {
    const handleScreenshotPreview = (event) => {
      const data = event.data || {};

      if (data.action === 'sendScreenShotPreview') {
        setViewportScreenshot(data.imageSrc || NO_SCREENSHOT);
      }
    };

    window.addEventListener('message', handleScreenshotPreview);

    // send message to take screen-shot
    sendPostMessage({
      action: 'takeScreenShot',
    });

    return () => window.removeEventListener('message', handleScreenshotPreview);
  }, []);

  const isScreenshotAvailable = useMemo(() => !isMobile() && viewportScreenshot && viewportScreenshot !== NO_SCREENSHOT, [viewportScreenshot]);

  const dataURItoBlob = (dataURI: string) => {
    let byteString;
    if (dataURI.split(',')[0].indexOf('base64') >= 0) {
      byteString = atob(dataURI.split(',')[1]);
    } else {
      byteString = unescape(dataURI.split(',')[1]);
    }
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const ia = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ia], { type: mimeString });
  };

  const getActiveFeature = () => {
    const allFeatures = settings.current;

    return Object.keys(allFeatures).reduce((features, key) => {
      if (allFeatures[key].value) {
        features[key] = true;
      }

      return features;
    }, {});
  };

  const submitForm = (event) => {
    event.preventDefault();
    setIsProcessing(true);

    const formData = new FormData();
    if (code) {
      formData.append('code', code);
    }
    if (href) {
      formData.append('url', href);
    }
    if (text) {
      formData.append('text', text);
    }
    if (email) {
      formData.append('email', email);
    } else {
      formData.append('email', '');
    }
    if (settings) {
      formData.append('features', JSON.stringify(getActiveFeature()));
    }
    if (viewportScreenshot && viewportScreenshot !== NO_SCREENSHOT) {
      formData.append('screenShot', dataURItoBlob(viewportScreenshot));
    }

    return Api.sendFormData('reports', formData)
      .then((response) => {
        clearErrors();
        if (response.errors) {
          setFormError(response.errors);
        }
        if (response.code === 200) {
          setText('');

          // $rootScope.$broadcast("showMessage", {
          //   type: "success",
          //   message: "widget.report.saved",
          // });
          goBack();
        }
      })
      .catch((error) => {
        // $rootScope.$broadcast("showMessage", {
        //   type: "error",
        //   body: error.message,
        // });
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  const onFocus = (e) => {
    e.target.parentElement && e.target.parentElement.classList.add('focus');
  };
  const onBlur = (e) => {
    e.target.parentElement && e.target.parentElement.classList.remove('focus');
  };
  useEffect(() => layoutStore.viewUpdate$.next(), [])
  if (!viewportScreenshot) {
    return <div className="widget-form widget-form_loading">
      <img src="./frontend/images/spinner.svg" alt="" width={40} aria-hidden="true" />
    </div>;
  }

  return (
      <div class="widget-form widget-form_report">
        <form
            name="form"
            role="form"
            onSubmit={submitForm}
            noValidate
            autocomplete="off"
        >
          <fieldset disabled={isProcessing}>
            <div
                input-focus
                auto-focus="true"
                class={`widget-input ${formError?.text ? 'has-error' : ''}`}
            >
              <label id="report-txt" class="label" for="text">
                {translate('widget.report.text.label')}
              </label>
              <textarea
                  onInput={(e) => setText((e.target as HTMLInputElement).value)}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  aria-labelledby="report-txt report-txt-err"
                  aria-invalid={formError?.text ? 'true' : 'false'}
                  title="{{ 'widget.report.text.label' | translate }}"
                  id="text"
                  name="text"
                  class="input input_textarea"
              />
              {formError?.text && (
                  <div id="report-txt-err" role="alert" class="help-block">
                    {translate(formError.text)}
                  </div>
              )}
            </div>
            <div
                input-focus
                class={`widget-input ${formError?.email ? 'has-error' : ''}`}
            >
              <label id="report-email" class="label" for="email">
                {translate('widget.report.email.label')}
              </label>
              <input
                  onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  aria-labelledby="report-email report-email-err"
                  aria-invalid={formError?.email ? 'true' : 'false'}
                  title={translate('widget.report.email.label')}
                  id="email"
                  name="email"
                  class="input"
                  placeholder={translate('widget.report.email.placeholder')}
                  type="email"
              />
              {formError?.email && (
                  <div id="report-email-err" role="alert" class="help-block">
                    {translate(formError.email)}
                  </div>
              )}
            </div>

            <div class="buttons">
              <button
                  class="btn btn_blue btn_border"
                  onClick={goBack}
                  type="button"
                  aria-label={translate('widget.report.buttons.cancel.aria')}
              >
                {translate('widget.report.buttons.cancel.label')}
              </button>
              <button
                  class={`btn btn_blue ${isProcessing ? 'processing' : ''}`}
                  type="submit"
                  aria-label={translate(
                    isProcessing
                      ? 'widget.report.buttons.submit.processing'
                      : 'widget.report.buttons.submit.aria',
                  )}
              >
                {translate(
                  isProcessing
                    ? 'widget.report.buttons.submit.processing'
                    : 'widget.report.buttons.submit.label',
                )}
              </button>
            </div>

            {isScreenshotAvailable && (
                <div class="screen-shot-wrapper">
                  <img
                      alt="A screenshot of this page"
                      src={viewportScreenshot}
                  />
                </div>
            )}
          </fieldset>
        </form>
      </div>
  );
};
